import React, { useState } from "react";
import DataTable from "react-data-table-component";

interface accessPermissions {
  permissionId: number;
  userTypeId: number;
  menuId: number;
  isAccessAllowed: boolean;
  menuName: string;
  parentMenuName: string;
  isMenu: boolean;
  isSubMenu: boolean;
  userTypeName: string;
}

interface AccessPermissionViewProps {
  accessPermissionData: accessPermissions[];
  onEditAccessPermission: (accessPermission: accessPermissions) => void;
  onAddClick: () => void;
  showAddButton: boolean;
}

const AccessPermissionView: React.FC<AccessPermissionViewProps> = ({
  accessPermissionData,
  onEditAccessPermission,
  onAddClick,
  showAddButton,
}) => {
  const [toggleCleared, setToggleCleared] = useState(false);

  // Handle row selection with proper typing
  const handleRowSelected = (state: { selectedRows: accessPermissions[] }) => {
    console.log("Selected rows:", state.selectedRows);
  };

  // Define columns for DataTable
  const columns = [
    {
      name: "Menu Name",
      selector: (row: accessPermissions) => row.menuName,
      sortable: true,
      cell: (row: accessPermissions) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              backgroundColor: "rgb(167 133 144)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "10px",
              fontWeight: "bold",
            }}
          >
            {row.menuName.charAt(0).toUpperCase()}
          </div>
          <span style={{ fontWeight: "500", color: "#33475b" }}>
            {row.menuName}
          </span>
        </div>
      ),
      width: "230px",
    },
    {
      name: "Parent Menu Name",
      selector: (row: accessPermissions) => row.parentMenuName || "N/A",
      sortable: true,
      cell: (row: accessPermissions) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              backgroundColor: "rgb(167 133 144)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "10px",
              fontWeight: "bold",
            }}
          >
            {row.parentMenuName
              ? row.parentMenuName.charAt(0).toUpperCase()
              : "N/A"}
          </div>
          <span style={{ fontWeight: "500", color: "#33475b" }}>
            {row.parentMenuName || "N/A"}
          </span>
        </div>
      ),
      width: "230px",
    },
    {
      name: "Access Allowed",
      selector: (row: accessPermissions) => row.isAccessAllowed,
      sortable: true,
      cell: (row: accessPermissions) => {
        const isAccessAllowed =
          typeof row.isAccessAllowed === "boolean"
            ? row.isAccessAllowed
            : row.isAccessAllowed === "true";
        return (
          <span
            className={isAccessAllowed ? "active-status" : "inactive-status"}
          >
            {isAccessAllowed ? "ALLOWED" : "DENIED"}
          </span>
        );
      },
      width: "150px",
    },
    {
      name: "User Type",
      selector: (row: accessPermissions) => row.userTypeName,
      sortable: true,
      width: "150px",
    },
    {
      name: "Menu",
      selector: (row: accessPermissions) => row.isMenu,
      sortable: true,
      cell: (row: accessPermissions) => {
        const isMenu =
          typeof row.isMenu === "boolean" ? row.isMenu : row.isMenu === "true";
        return (
          <span className={isMenu ? "active-status" : "inactive-status"}>
            {isMenu ? "YES" : "NO"}
          </span>
        );
      },
      width: "100px",
    },
    {
      name: "Submenu",
      selector: (row: accessPermissions) => row.isSubMenu,
      sortable: true,
      cell: (row: accessPermissions) => {
        const isSubMenu =
          typeof row.isSubMenu === "boolean"
            ? row.isSubMenu
            : row.isSubMenu === "true";
        return (
          <span className={isSubMenu ? "active-status" : "inactive-status"}>
            {isSubMenu ? "YES" : "NO"}
          </span>
        );
      },
      width: "100px",
    },
    {
      name: "",
      cell: (row: accessPermissions) => (
        <div className="action-cell">
          <button
            className="action-button edit-button"
            title="Edit Access Permission"
            onClick={() => onEditAccessPermission(row)}
          >
            <i className="bi bi-pencil-square"></i>
          </button>
          <button className="action-button more-button" title="Replace icon">
            <i className="bi bi-three-dots"></i>
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "60px",
      className: "sticky-action-column",
    },
  ];

  return (
    <div className="card">
      <div className="card-header carHeaderWithBtn d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">Access Permissions List</h5>
        {showAddButton && (
          <button
            type="button"
            className="demo-btn btn btn-outline-primary btn-sm"
            onClick={onAddClick}
          >
            <i className="fas fa-plus me-1"></i>+ Add Access Permission
          </button>
        )}
      </div>
      <div className="card-body accesspermission-cardbody p-0">
        <DataTable
          columns={columns}
          data={accessPermissionData}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[5, 10, 15, 20]}
          highlightOnHover
          striped
          responsive
          noDataComponent={
            <div className="text-center py-4">
              <p className="text-muted mb-0">No access permissions found.</p>
            </div>
          }
          selectableRows
          onSelectedRowsChange={handleRowSelected}
          clearSelectedRows={toggleCleared}
        />
      </div>
    </div>
  );
};

export default AccessPermissionView;
